import { useQuery } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useAuth } from "../auth/AuthContext";
import { MainCard } from "../components/MainCard";

/** Mirrors `apps/admin-web/src/pages/UsagePage.tsx`'s `METRIC_LABELS` verbatim (falls back to the raw
 *  metric id for an unrecognized/future metric). */
const METRIC_LABELS: Record<string, string> = {
  "listen.start": "Narration/audio listens started",
  export: "Progress exports",
  print: "Prints",
  "catalog.update.applied": "Catalog updates applied",
};

/**
 * Usage tab, re-skinned to MUI/Mantis (docs/337-ADMIN-MANTIS-MIGRATION.md v2) — mirrors
 * `apps/admin-web/src/pages/UsagePage.tsx` (itself mirroring `apps/admin/app/(tabs)/usage.tsx` /
 * `specs/admin/screens/usage.md`): a read-only aggregate over the `usage_counters` table (docs/310) +
 * the tracking-disclosure acceptance count by version (docs/300). The `useQuery` here is lifted
 * UNCHANGED — only the returned JSX differs.
 */
export function UsagePage() {
  const { client, token } = useAuth();

  const statsQuery = useQuery({
    queryKey: ["admin-usage-stats"],
    queryFn: () => client!.adminUsageStats(token!),
    enabled: !!client && !!token,
  });
  const stats = statsQuery.data;
  const totals = stats?.totals ?? [];
  const disclosureAcceptance = stats?.disclosureAcceptance ?? [];
  const totalCount = totals.reduce((sum, row) => sum + row.count, 0);

  return (
    <>
      <MainCard
        title="Usage"
        sx={{ mb: 2 }}
        secondary={
          <Button size="small" variant="outlined" color="secondary" onClick={() => statsQuery.refetch()}>
            Refresh
          </Button>
        }
      >
        {statsQuery.isError ? <Alert severity="error">{(statsQuery.error as Error).message}</Alert> : null}
        {statsQuery.isLoading ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : totalCount === 0 ? (
          <Typography color="text.secondary" sx={{ mb: 0 }}>
            No usage recorded yet.
          </Typography>
        ) : (
          <Stack spacing={1}>
            {totals.map((row) => (
              <Box key={row.metric} sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography>{METRIC_LABELS[row.metric] ?? row.metric}</Typography>
                <Typography color="text.secondary">{row.count}</Typography>
              </Box>
            ))}
          </Stack>
        )}
      </MainCard>

      <MainCard title="Tracking disclosure acceptance">
        {disclosureAcceptance.length === 0 ? (
          <Typography color="text.secondary" sx={{ mb: 0 }}>
            No acceptances recorded yet.
          </Typography>
        ) : (
          <Stack spacing={1}>
            {disclosureAcceptance.map((row) => (
              <Box key={row.version} sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography>v{row.version}</Typography>
                <Typography color="text.secondary">{row.count}</Typography>
              </Box>
            ))}
          </Stack>
        )}
      </MainCard>
    </>
  );
}
